import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ClipboardCopy, ChevronDown, MessageSquare, Mail, Instagram, MessageCircle } from "lucide-react";

/**
 * "DM Copy" split button for the Admin → Verifications row. One tap copies a
 * ready-to-paste message with the business name and claim link already
 * filled in. Choose tone via the dropdown:
 *   - Short (Instagram DM / SMS)
 *   - Long (Facebook / LinkedIn / long-form)
 *   - Text (very short SMS)
 *   - Email (formal / cold outreach)
 *
 * All links carry `?claim=1&ref=<admin_referral_code>` so signups credit
 * the admin's TBC Points (currently 50 on signup, +100 on first paid month).
 * The admin's ref code is fetched once from /api/referrals/me and cached in
 * a module-level ref so we don't refetch per-row.
 */

let cachedRefCode = null;
let cachedRefPromise = null;

async function fetchAdminRefCode() {
  if (cachedRefCode !== null) return cachedRefCode;
  if (cachedRefPromise) return cachedRefPromise;
  cachedRefPromise = axios
    .get(`${API}/referrals/me`, { withCredentials: true })
    .then((r) => {
      cachedRefCode = r.data?.code || "";
      return cachedRefCode;
    })
    .catch(() => {
      cachedRefCode = ""; // don't retry — non-fatal
      return "";
    })
    .finally(() => {
      cachedRefPromise = null;
    });
  return cachedRefPromise;
}

const templates = {
  short: ({ name, url }) =>
    `Hey! We spotted ${name} and reserved a free profile for you on The Blk Community — a directory built to spotlight Black-owned businesses. Claim it in under a minute (photos, hours, respond to reviews):\n${url}`,

  long: ({ name, url }) =>
    `Hi — I'm Matt with The Blk Community, a national directory built to help customers discover Black-owned businesses. We already listed ${name} and reserved an owner profile for you.\n\nClaim it in about 30 seconds — no fees, no card required. Once claimed you can update photos, hours, respond to reviews, and (if you want) sell products directly through the platform.\n\n👉 ${url}\n\nWould love to have you in — reply here if you have any questions.`,

  text: ({ name, url }) =>
    `Hi from The Blk Community 👋 We reserved a free profile for ${name}. Claim it in ~30 sec: ${url}`,

  email: ({ name, url }) =>
    `Hello,\n\nMy name is Matt Walker, founder of The Blk Community — a national directory and marketplace built to spotlight Black-owned businesses. We featured ${name} in our launch dataset and reserved an owner profile for you.\n\nThere is no cost to claim it. Doing so unlocks the ability to update your storefront details, add products, and appear in our weekly featured emails.\n\nClaim link (30 seconds): ${url}\n\nHappy to answer any questions.\n\n— Matt Walker (King Midas)\nFounder, The Blk Community (a Midas Touch Group LLC company)`,
};

const options = [
  { key: "short", label: "Short DM", desc: "Instagram / short-form", icon: Instagram },
  { key: "long", label: "Long DM", desc: "Facebook / LinkedIn", icon: MessageSquare },
  { key: "text", label: "SMS Text", desc: "1-liner for text messages", icon: MessageCircle },
  { key: "email", label: "Email", desc: "Formal / cold outreach", icon: Mail },
];

export default function AdminDMCopyButton({ business, className = "" }) {
  const [refCode, setRefCode] = useState(cachedRefCode || "");
  const [defaultChoice, setDefaultChoice] = useState(() => {
    try {
      return localStorage.getItem("tbc.admin_dm_default") || "short";
    } catch {
      return "short";
    }
  });

  useEffect(() => {
    // Warm the ref-code cache in the background so the first click is instant.
    fetchAdminRefCode().then(setRefCode);
  }, []);

  const buildUrl = (code) => {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://theblkcommunity.com";
    const base = `${origin}/business/${business.business_id}?claim=1`;
    return code ? `${base}&ref=${code}` : base;
  };

  const copyMessage = async (key) => {
    const code = refCode || (await fetchAdminRefCode());
    setRefCode(code);
    const url = buildUrl(code);
    const msg = templates[key]({ name: business.name, url });
    try {
      await navigator.clipboard.writeText(msg);
      const label = options.find((o) => o.key === key)?.label || "Message";
      toast.success(`${label} copied — ready to paste.`);
    } catch {
      toast.error("Couldn't access clipboard — try again after tapping the page.");
    }
    // Remember the last-used variant so the primary button repeats it.
    try {
      localStorage.setItem("tbc.admin_dm_default", key);
      setDefaultChoice(key);
    } catch { /* localStorage may be blocked */ }
  };

  const primaryLabel = options.find((o) => o.key === defaultChoice)?.label || "DM Copy";

  return (
    <div className={`inline-flex items-stretch rounded-md border border-tbc-navy overflow-hidden ${className}`} data-testid={`dm-copy-${business.business_id}`}>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => copyMessage(defaultChoice)}
        className="h-8 text-xs px-2 rounded-none text-tbc-navy hover:bg-tbc-navy/5"
        data-testid={`dm-copy-primary-${business.business_id}`}
        title={`Copy ${primaryLabel} to clipboard`}
      >
        <ClipboardCopy className="w-3 h-3 mr-1" />
        {primaryLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-1.5 rounded-none border-l border-tbc-navy/30 text-tbc-navy hover:bg-tbc-navy/5"
            data-testid={`dm-copy-menu-${business.business_id}`}
            title="Pick a different format"
          >
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Copy claim message for</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((o) => {
            const Icon = o.icon;
            return (
              <DropdownMenuItem
                key={o.key}
                onClick={() => copyMessage(o.key)}
                className="cursor-pointer"
                data-testid={`dm-copy-option-${o.key}`}
              >
                <Icon className="w-3.5 h-3.5 mr-2 text-tbc-gold" />
                <div className="flex flex-col">
                  <span className="text-sm">{o.label}</span>
                  <span className="text-[10px] text-gray-500">{o.desc}</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
